import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import type { Company } from '../../../@types/Company';
import type { OfferDetails } from '../../../services/paymentApi';
import { paymentApi } from '../../../services/paymentApi';
import { ROUTES } from '../../../utils/routes';
import { hasActiveSubscription } from '../../../utils/subscriptions';
import Spinner from '../../common/Spinner';

interface SubscriptionBannerProps {
  company: Company | null;
  jwt: string | null;
}

const SubscriptionBanner: React.FC<SubscriptionBannerProps> = ({ company, jwt }) => {
  const [offer, setOffer] = useState<OfferDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    const fetchOffer = async() => {
      if (!jwt || !company || hasActiveSubscription(company)) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const data = await paymentApi.getOffer(company.id, jwt);
        setOffer(data);
      } catch (err) {
        console.error('Error fetching offer details:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchOffer();
  }, [jwt, company]);

  if (!company || !company.id || hasActiveSubscription(company)) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-rgpd-blue to-rgpd-blue-dark p-8 text-white shadow-md">
        <div className="absolute inset-0 opacity-[0.07]">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="relative z-10 text-center">
          <p className="mb-4 text-lg">Une erreur est survenue lors du chargement de l'offre.</p>
          <Link
            to={ROUTES.PAYMENT_CHECKOUT}
            className="inline-block rounded-lg bg-white px-6 py-2.5 text-sm font-bold text-rgpd-blue shadow-sm transition-all duration-200 hover:shadow-md"
          >
            Voir les offres
          </Link>
        </div>
      </div>
    );
  }

  const formattedPrice = (offer.AmountRecurrence / 100).toFixed(0);

  return (
    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-rgpd-blue to-rgpd-blue-dark p-8 text-white shadow-md">
      <div className="absolute inset-0 opacity-[0.07]">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      </div>
      
      <div className="relative z-10 flex flex-col items-center justify-between gap-8 lg:flex-row">
        <div className="flex-1 text-center lg:text-left">
          <h2 className="mb-2 text-2xl font-bold">
            Débloquez tout le potentiel du module Croissance
          </h2>
          <p className="mb-4 text-sm text-blue-100">
            Accédez à tous les modules, au support prioritaire et aux fonctionnalités avancées pour développer votre activité.
          </p>
          <div className="flex flex-wrap justify-center gap-2 text-sm lg:justify-start">
            {['Communication illimitée', 'Prospection avancée', 'Monitoring en temps réel'].map((label) => (
              <div key={label} className="flex items-center gap-1.5 rounded-full bg-white/15 px-3.5 py-1.5">
                <svg className="h-3.5 w-3.5 text-emerald-300" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                <span className="text-xs">{label}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex flex-col items-center gap-3">
          <div className="min-w-[180px] rounded-xl bg-white px-6 py-5 text-center text-rgpd-blue-dark shadow-lg">
            <div className="text-[42px] leading-none font-black">{formattedPrice}€</div>
            <div className="mt-0.5 text-[13px] font-semibold opacity-70">HT / mois</div>
            <div className="mt-0.5 text-[11px] opacity-50">Engagement 12 mois</div>
            <div className="mt-3.5 border-t border-gray-200 pt-3.5">
              <div className="text-[11px] text-gray-500">+ Frais fixes de paramétrage</div>
              <div className="text-[22px] leading-none font-extrabold text-rgpd-blue-dark">990€</div>
              <div className="mt-0.5 text-[11px] opacity-50">HT · une seule fois</div>
            </div>
          </div>
          
          <Link
            to={ROUTES.PAYMENT_CHECKOUT}
            className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 text-sm font-bold text-rgpd-blue shadow-sm transition-all duration-200 hover:bg-gray-50 hover:shadow-md"
          >
            S&apos;abonner maintenant
          </Link>
          
          <p className="text-center text-[10px] text-blue-200">
            Paiement sécurisé · Résiliation simple · Support inclus
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionBanner;


